import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthenticationResult } from '@app/shared/interfaces/user-response.interface';
import { User } from '@app/shared/models/user-info.model';
import { API, API_ROUTES } from '@app/shared/utils/api-routes';
import { environment } from '@environments/environment';
import { jwtDecode } from 'jwt-decode';
import { DateTime } from 'luxon';
import { BehaviorSubject, Observable } from 'rxjs';
import { SessionStorageService } from './session-storage.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public static ID_TOKEN = 'idToken';

  public static ACCESS_TOKEN = 'accessToken';

  public static REFRESH_TOKEN = 'refreshToken';

  public static CREATED_IN = 'date';

  public connectedUser$: BehaviorSubject<User | null>;

  public currentUser: Observable<User | null>;

  private authJwtDecoded = {
    auth_time: '',
    email_verified: false,
    sub: '',
    email: '',
  };

  constructor(
    private http: HttpClient,
    public router: Router,
    public sessionStorage: SessionStorageService,
  ) {
    const savedToken = sessionStorage.get(AuthService.ID_TOKEN);
    const user = this.createUserFromToken(savedToken ?? '');
    this.connectedUser$ = new BehaviorSubject<User | null>(user);
    this.currentUser = this.connectedUser$.asObservable();
  }

  // SSO
  responseLoginSaml(authenticationResult: AuthenticationResult) {
    const { idToken, accessToken, refreshToken } = authenticationResult;
    if (idToken) {
      this.sessionStorage.set(AuthService.ACCESS_TOKEN, accessToken);
      this.sessionStorage.set(AuthService.ID_TOKEN, idToken);
      if (refreshToken) {
        this.sessionStorage.set(AuthService.REFRESH_TOKEN, refreshToken);
        this.sessionStorage.set(AuthService.CREATED_IN, DateTime.now().toISO());
      }

      const user = this.createUserFromToken(idToken);
      this.connectedUser$.next(user);
    }
  }

  public samlAuth(code: string): Observable<AuthenticationResult> {
    return this.http.post<AuthenticationResult>(
      `${environment.BA_API_BASE_URL}${API.IAM}${API_ROUTES.SAML_AUTH}`,
      {},
      {
        params: { code },
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      },
    );
  }

  // Refresh Token
  public refreshToken(): Observable<AuthenticationResult> {
    const refreshToken = this.getRefreshToken();
    return this.http.post<AuthenticationResult>(`${environment.BA_API_BASE_URL}${API.IAM}${API_ROUTES.REFRESH_TOKEN}`, {
      refreshToken,
    });
  }

  public handleRefreshResponse(authResult: AuthenticationResult) {
    const { idToken, accessToken, refreshToken } = authResult;
    if (idToken) {
      this.sessionStorage.set(AuthService.ID_TOKEN, idToken);
      this.sessionStorage.set(AuthService.ACCESS_TOKEN, accessToken);
      if (refreshToken) {
        this.sessionStorage.set(AuthService.REFRESH_TOKEN, refreshToken);
      }
      this.sessionStorage.set(AuthService.CREATED_IN, DateTime.now().toISO());

      const user = this.createUserFromToken(idToken);
      this.connectedUser$.next(user);
    }
  }

  clear() {
    this.emptySession();
    this.connectedUser$.next(null);
  }

  public createUserFromToken(idToken: string): User | null {
    if (!idToken) {
      return null;
    }

    this.authJwtDecoded = jwtDecode(idToken);
    return {
      authTime: this.authJwtDecoded.auth_time,
      email: this.authJwtDecoded.email,
      emailVerified: this.authJwtDecoded.email_verified,
      authJwt: idToken,
      id: this.authJwtDecoded.sub,
    };
  }

  public emptySession() {
    this.sessionStorage.remove(AuthService.ACCESS_TOKEN);
    this.sessionStorage.remove(AuthService.ID_TOKEN);
    this.sessionStorage.remove(AuthService.REFRESH_TOKEN);
    this.sessionStorage.remove(AuthService.CREATED_IN);
  }

  public isSessionExpired(): boolean {
    const createdInValue = this.sessionStorage.get(AuthService.CREATED_IN);
    const accessToken = this.sessionStorage.get(AuthService.ACCESS_TOKEN);

    if (!createdInValue || !accessToken) {
      return true;
    }

    const createdIn = DateTime.fromISO(createdInValue);
    const now = DateTime.now();

    const sessionExpired = createdIn < now.minus({ day: environment.USER_SESSION_EXPIRY_DELAY_DAYS });

    const tokenExpired = this.isTokenExpired(accessToken);

    return sessionExpired || tokenExpired;
  }

  public isLoggedIn(): boolean {
    return this.sessionStorage.get(AuthService.ID_TOKEN) !== null;
  }

  public getToken() {
    return this.sessionStorage.get(AuthService.ID_TOKEN);
  }

  public getRefreshToken() {
    return this.sessionStorage.get(AuthService.REFRESH_TOKEN);
  }

  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));

      if (!payload.exp) {
        return true;
      }

      const expiration = DateTime.fromSeconds(payload.exp);

      return expiration <= DateTime.now().plus({ minute: 1 });
    } catch {
      return true;
    }
  }
}
